import { CRM_FRAME_PATH } from '../crm/crmBridge'
import type { Range } from './types'

const usdWhole = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
const usdCents = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 })
const int = new Intl.NumberFormat('en-US')

/** $120 or $120.50: whole dollars without cents, otherwise always two decimals. */
export const money = (n: number | null | undefined) => (n == null ? '—' : Number.isInteger(n) ? usdWhole.format(n) : usdCents.format(n))
export const count = (n: number | null | undefined) => (n == null ? '—' : int.format(n))
export const percent = (ratio: number | null | undefined) => (ratio == null ? '—' : `${Math.round(ratio * 1000) / 10}%`)

/** YYYY-MM-DD of a Date in UTC (money is cut at UTC midnight). */
export const utcDay = (d: Date) => d.toISOString().slice(0, 10)

export const RANGE_PRESETS = ['all', 'today', 'yesterday', '7', '30', 'month', 'lastMonth', 'custom'] as const
export type RangePreset = (typeof RANGE_PRESETS)[number]

/** The range a preset stands for, in UTC days, today inclusive ('custom' has none of its own). */
export function presetRange(preset: RangePreset, now = new Date()): Range {
  const day = (offset: number) => utcDay(new Date(now.getTime() + offset * 86_400_000))
  const y = now.getUTCFullYear()
  const m = now.getUTCMonth()
  switch (preset) {
    case 'today':
      return { from: day(0), to: day(0) }
    case 'yesterday':
      return { from: day(-1), to: day(-1) }
    case '7':
    case '30':
      return { from: day(1 - Number(preset)), to: day(0) }
    case 'month':
      return { from: utcDay(new Date(Date.UTC(y, m, 1))), to: day(0) }
    case 'lastMonth':
      return { from: utcDay(new Date(Date.UTC(y, m - 1, 1))), to: utcDay(new Date(Date.UTC(y, m, 0))) }
    default:
      return { from: null, to: null }
  }
}

/** A preset and its custom days, as the page keeps them. */
export interface RangeChoice {
  preset: RangePreset
  custom: Range
}

export const rangeOf = (c: RangeChoice): Range => (c.preset === 'custom' ? c.custom : presetRange(c.preset))

/**
 * Daily points as a continuous series from `from` to `to` (missing days are 0),
 * grouped by week when it would be more than `maxBars` bars. Labelled by first day.
 */
export function fillDays<T extends { day: string; earnings: number; fans: number }>(
  points: T[],
  from: string | null,
  to: string | null,
  maxBars = 120,
): Array<{ day: string; earnings: number; fans: number }> {
  if (!points.length) return []
  const start = Date.parse(`${from ?? points[0]!.day}T00:00:00Z`)
  const end = Date.parse(`${to ?? utcDay(new Date())}T00:00:00Z`)
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return points
  const byDay = new Map(points.map((p) => [p.day, p]))
  const days: Array<{ day: string; earnings: number; fans: number }> = []
  for (let t = start; t <= end; t += 86_400_000) {
    const day = utcDay(new Date(t))
    const p = byDay.get(day)
    days.push({ day, earnings: p?.earnings ?? 0, fans: p?.fans ?? 0 })
  }
  if (days.length <= maxBars) return days
  const weeks: typeof days = []
  for (let i = 0; i < days.length; i += 7) {
    const slice = days.slice(i, i + 7)
    weeks.push({
      day: slice[0]!.day,
      earnings: Math.round(slice.reduce((s, d) => s + d.earnings, 0) * 100) / 100,
      fans: slice.reduce((s, d) => s + d.fans, 0),
    })
  }
  return weeks
}

/** OnlyFans' own URL for a link: onlyfans.com/<username>/c<campaign code>. */
export function linkUrl(username: string | null, campaignCode: number | null | undefined): string | null {
  if (!username || campaignCode == null) return null
  return `https://onlyfans.com/${username}/c${campaignCode}`
}

/** OnlyFans' own URL for a trial link: onlyfans.com/<username>/trial/<code>. */
export function trialUrl(username: string | null, code: string | null | undefined): string | null {
  if (!username || !code) return null
  return `https://onlyfans.com/${username}/trial/${code}`
}

/**
 * The public URL of a report. `publicBase` is the reports host the CRM is configured
 * with (LINK_REPORTS_BASE_URL); without one (development) the CRM serves it behind
 * the console's own CRM path.
 */
export function reportUrl(token: string, publicBase: string | null, origin = window.location.origin): string {
  if (publicBase) return `${publicBase.replace(/\/+$/, '')}/r/${token}`
  return `${origin}${CRM_FRAME_PATH.replace(/\/?$/, '/')}r/${token}`
}
